// Central error → user-facing copy resolver.
//
// Cascade: (code + vía + motivo) → (code + vía + reason) → (code + vía) → (code + reason)
//   → (vía + status, solo sin código útil) → code → status → errors.server.generic.
// CRITICAL: this function NEVER reads err.message. The backend's `message`
// field exists for non-SPA API consumers (curl, integrators); the SPA always
// translates from the machine-readable `code`. This guarantees no technical
// string ever reaches a user-facing toast.

import type { TFunction } from 'i18next';
import i18n from '@/i18n';

export interface ApiErrorLike {
  status?: number;
  code?: string;
  details?: {
    eta?: string;
    retry_after_sec?: number;
    fields?: Array<{ path: string[]; message: string }>;
    [k: string]: unknown;
  } | Record<string, unknown>;
  message?: string;
  ref?: string;
  hint?: string;
  /**
   * Código del permiso que exigía el gate (`inventory.edit`, `crm.delete`…). Se preserva
   * porque forma parte del contrato del error y sirve para diagnosticar, pero NO se
   * renderiza: es un identificador de máquina. Ver la nota en `resolveErrorMessage` y el
   * test que fija que nunca llega al mensaje.
   */
  required?: string;
  /**
   * Variante del error dentro de un mismo `code`. Hoy solo `EMISSION_REQUIRES_CERT`:
   * 'no_cert' (subir el certificado) / 'expired' (renovarlo). Son DOS acciones
   * distintas para el usuario, y el backend ya las distingue.
   */
  reason?: string;
}

export type ErrorSeverity = 'info' | 'warning' | 'error';

export interface ResolvedError {
  title: string;
  description?: string;
  severity: ErrorSeverity;
  /**
   * `true` cuando no ha habido copy específico para este error y el mensaje sale
   * de un `*.generic` — «Esta acción no se puede completar en este momento»,
   * «Revisa los campos marcados». Son los toasts que dejan al usuario y al que
   * le da soporte sin nada: ni qué falló, ni dónde mirar.
   *
   * `toastApiError` lo usa para añadir la referencia técnica SOLO en ese caso.
   * Cuando el copy sí explica el problema, el toast se queda como está.
   */
  isGeneric: boolean;
  /**
   * `true` cuando título + descripción son un literal validado ENTERO (bloqueos de
   * VERI*FACTU). Quien lo pinte no le antepone otro título: el aviso tiene que ser el literal.
   */
  literalEntero: boolean;
}

interface KeyMap { key: string; severity: ErrorSeverity; literalEntero?: true }

// Severity rationale:
//  - 'info'    → intentional state, not a failure (coming soon, upgrade, MFA prompt).
//  - 'warning' → user-actionable issue (validation, duplicate, rate limit, maintenance).
//  - 'error'   → unexpected failure or genuine permission/auth block.
// 🔴 EXPORTADO desde el 14-10-2026, y no es un detalle de estilo: el test que compara los
// literales de este catálogo con los del backend (`literalesDelBackendCoinciden.test.ts`)
// necesita el mapa REAL código → clave. Con una copia a mano en el test, un código nuevo no
// entraría en ella y el test seguiría verde sobre un texto que nadie compara.
export const CODE_TO_KEY: Record<string, KeyMap> = {
  // Auth
  AUTH_REQUIRED:              { key: 'errors.auth.session_expired',     severity: 'error' },
  AUTH_SESSION_EXPIRED:       { key: 'errors.auth.session_expired',     severity: 'error' },
  AUTH_BAD_CREDENTIALS:       { key: 'errors.auth.bad_credentials',     severity: 'error' },
  AUTH_MFA_REQUIRED:          { key: 'errors.auth.mfa_required',        severity: 'info' },
  CSRF_INVALID:               { key: 'errors.auth.session_expired',     severity: 'error' },
  PERMISSION_DENIED:          { key: 'errors.auth.permission_denied',   severity: 'error' },
  // El backend lo emite (middleware/auth.ts) cuando la sesión no tiene empresa activa.
  // Sin esta entrada caía al 403 genérico y decía "no tienes permiso", que es una pista
  // falsa: el problema no es el permiso, es que no hay empresa donde ejercerlo.
  NO_ACTIVE_COMPANY:          { key: 'errors.auth.no_active_company',   severity: 'warning' },
  // 403 de `requireCompanyTaxId`: la empresa no tiene NIF/CIF. Sin esta entrada
  // caería al 403 genérico —"no tienes permiso"—, que es la pista falsa exacta
  // que ya documenta NO_ACTIVE_COMPANY justo aquí arriba: no es un permiso que
  // falte, es un dato. Quien lo ve es admin/owner, o sea quien puede arreglarlo.
  COMPANY_TAX_ID_REQUIRED:    { key: 'errors.auth.company_tax_id_required', severity: 'warning' },
  SUBSCRIPTION_REQUIRED:      { key: 'errors.feature.upgrade_required', severity: 'info' },
  // Resource
  RESOURCE_NOT_FOUND:         { key: 'errors.resource.not_found',         severity: 'warning' },
  RESOURCE_DUPLICATE:         { key: 'errors.resource.duplicate',         severity: 'warning' },
  RESOURCE_DUPLICATE_DELETED: { key: 'errors.resource.duplicate_deleted', severity: 'info' },
  RESOURCE_IN_USE:            { key: 'errors.resource.in_use',            severity: 'warning' },
  RESOURCE_LOCKED:            { key: 'errors.resource.locked',            severity: 'warning' },
  RESOURCE_CROSS_TENANT:      { key: 'errors.resource.not_found',         severity: 'warning' },
  // Validation / business
  VALIDATION_FAILED:          { key: 'errors.validation.generic',           severity: 'warning' },
  BUSINESS_RULE_VIOLATED:     { key: 'errors.business.generic',             severity: 'error' },
  INVOICE_STATE_INVALID:      { key: 'errors.business.invoice_state_invalid', severity: 'error' },
  INVOICE_NOT_SENT:           { key: 'errors.business.invoice_not_sent',      severity: 'warning' },
  // #978 — solo se rectifican facturas emitidas/cobradas (sent/partial/paid); borradores y anuladas no.
  INVOICE_NOT_RECTIFIABLE:    { key: 'errors.business.invoice_not_rectifiable', severity: 'warning' },
  // Capa de migración (#2) — InvoiceImportedReadonlyError (409): factura importada
  // (libro histórico migrado) es solo-lectura; no se edita/anula/borra.
  INVOICE_IMPORTED_READONLY:  { key: 'errors.business.invoice_imported_readonly', severity: 'warning' },
  // #652 — inalterabilidad de la factura emitida (RD 1619/2012; Ley 11/2021). Lo lanzan
  // TODOS los caminos de escritura: PUT/DELETE de la factura, el router de líneas
  // (/invoice-items) y el bulk. Sin esta entrada el usuario veía el código crudo.
  INVOICE_IMMUTABLE:          { key: 'errors.business.invoice_immutable',          severity: 'warning' },
  // verix-api#3500 (#3409) — tipos de cambio. Los dos estaban sin mapear: el 422 de emisión
  // en divisa caía al genérico, y el 403 del alta de tipos al de permisos («concédelo en
  // Roles y Permisos»), que manda a buscar un permiso que no lo resuelve. La tabla de tipos
  // es GLOBAL y ya solo la escribe soporte.
  INVOICE_FX_RATE_UNAVAILABLE: { key: 'errors.business.invoice_fx_rate_unavailable', severity: 'warning' },
  EXCHANGE_RATE_PLATFORM_ONLY: { key: 'errors.business.exchange_rate_platform_only', severity: 'info' },
  // #978 PR-4 — validaciones formales de rectificativas (RD 1619/2012 / LIVA art.89).
  RECTIFICATION_EXPIRED:        { key: 'errors.business.rectification_expired',        severity: 'warning' },
  INVALID_RECTIFICATION_SERIES: { key: 'errors.business.invalid_rectification_series', severity: 'warning' },
  RECTIFICATION_INCONSISTENT:   { key: 'errors.business.rectification_inconsistent',   severity: 'error' },
  // verix-api#3599 — original sin moneda (anterior a #3543) en una sociedad no-EUR: se
  // contabilizó en la funcional y se declaró como EUR, y la API se niega a rectificarla.
  // Sin esta entrada caía al genérico de 422 («Revisa los campos marcados»).
  RECTIFICATION_LEGACY_CURRENCY: { key: 'errors.business.rectification_legacy_currency', severity: 'warning' },
  // #653 slice 7.5 — autoliquidación rectificativa del 303 (RD 117/2024). 422 del BE.
  NO_PRIOR_FILING_TO_RECTIFY:           { key: 'errors.business.no_prior_filing_to_rectify',           severity: 'warning' },
  RECTIFICATIVA_DEVOLUCION_UNCONFIRMED: { key: 'errors.business.rectificativa_devolucion_unconfirmed', severity: 'warning' },
  // verix-api#2944 — fijar el tipo reducido del 130 en una actividad exige confirmar el
  // derecho del art. 68.4 LIRPF. Sin esta entrada caía al 422 genérico, que no dice qué marcar.
  TIPO_REDUCIDO_SIN_CONFIRMAR: { key: 'errors.business.tipo_reducido_sin_confirmar', severity: 'warning' },
  // Rechazo de validación de AEAT (VeriFACTU): permanente, requiere subsanar el dato. NO es
  // transitorio → no mostrar el genérico "vuelve a intentarlo". El motivo real viaja en
  // details.aeat_detail (placeholder {{detail}}).
  AEAT_VALIDATION_REJECTED:     { key: 'errors.business.aeat_rejected',               severity: 'error' },
  // Fallo de la conexión bancaria (Powens) que NO se reintenta solo: credenciales
  // rechazadas, consentimiento revocado... El usuario debe actuar, así que no vale el
  // DEPENDENCY_DOWN genérico ("se reintentará automáticamente" sería mentira). El motivo
  // real viaja en details.powens_detail (placeholder {{detail}}).
  BANK_CONNECTION_FAILED:       { key: 'errors.business.bank_connection_failed',      severity: 'error' },
  // ⚠️ El código está MAL NOMBRADO en el backend y su copy mentía en consecuencia.
  // `AppError.companyNoNif()` tiene un único call site (`routes/invoicing/invoices.ts`)
  // y lo que comprueba es `invoice.customers?.nif` — el NIF del CLIENTE, no el de la
  // empresa. El texto mandaba a «Ajustes → Empresa», donde el CIF ya estaba bien: un
  // callejón sin salida en el día 1. También salta cuando la factura no tiene cliente
  // (`customer_id` es opcional en el schema), así que el copy cubre los dos casos.
  // `CUSTOMER_NO_NIF` queda mapeado de antemano por si el backend renombra el código.
  COMPANY_NO_NIF:             { key: 'errors.business.company_no_nif',        severity: 'warning' },
  CUSTOMER_NO_NIF:            { key: 'errors.business.customer_no_nif',       severity: 'warning' },
  // Gate de identificación del destinatario (art. 6.1.d y 4.4.a RD 1619/2012). Sin
  // mapeo caían al genérico de 422 «Revisa los campos marcados», y el campo a
  // corregir no está en la factura: está en la ficha del cliente o en la serie.
  CUSTOMER_NIF_INVALID:       { key: 'errors.business.customer_nif_invalid',   severity: 'warning' },
  // NIF con el carácter de control incorrecto (criterio de la asesoría, 21-10-2026). Los dos
  // son literales VALIDADOS: se pintan enteros y sin ofrecer corregir el último carácter.
  // El primero sale al emitir; el segundo al guardar la ficha del cliente o proveedor.
  CUSTOMER_NIF_CONTROL_INCORRECT: { key: 'errors.business.customer_nif_control_incorrect', severity: 'warning', literalEntero: true },
  CONTACT_NIF_CONTROL_INCORRECT:  { key: 'errors.business.contact_nif_control_incorrect',  severity: 'warning', literalEntero: true },
  CUSTOMER_NO_EU_VAT:         { key: 'errors.business.customer_no_eu_vat',     severity: 'warning' },
  SIMPLIFIED_NOT_ALLOWED:     { key: 'errors.business.simplified_not_allowed', severity: 'warning' },
  // CIF/NIF de la PROPIA empresa con el carácter de control incorrecto (verix-api,
  // `PUT /companies/me` y `POST /companies`). Literal validado para la ficha de un
  // contacto, PENDIENTE de validación para este sitio. Entero y sin propuesta.
  COMPANY_NIF_CONTROL_INCORRECT: { key: 'errors.business.company_nif_control_incorrect', severity: 'warning', literalEntero: true },
  COMPANY_WITHOUT_IBAN:       { key: 'errors.business.company_without_iban',  severity: 'warning' },
  EMPLOYEES_WITHOUT_IBAN:     { key: 'errors.business.employees_without_iban', severity: 'warning' },
  PERIOD_CLOSED:              { key: 'errors.business.period_closed',         severity: 'warning' },
  // Canonical backend code thrown by PeriodLockedError (assertPeriodNotLocked,
  // 422). Without this entry it fell through to the 422 status fallback
  // (errors.validation.generic → "Revisa los campos marcados"), so editing an
  // expense/entry dated in a closed period showed a meaningless toast with no
  // field highlighted (agentic-erp#1323).
  PERIOD_LOCKED:              { key: 'errors.business.period_closed',         severity: 'warning' },
  // Guardas del importador del asiento de apertura. MISMO hueco que los dos de
  // arriba, tercera vez: la ruta devolvía `{ error: 'fiscal_period_not_found' }`
  // sin `code`, así que `extractErrorInfo` dejaba `err.code` undefined y esto caía
  // al fallback de 422 → "Revisa los campos marcados". El usuario se ponía a
  // revisar el mapeo de columnas (que estaba bien) mientras el motivo real —no
  // existe el ejercicio fiscal— viajaba en la respuesta y se descartaba.
  // El backend ya emite AppError con estos códigos y el año en `details`.
  FISCAL_PERIOD_NOT_FOUND:    { key: 'errors.business.fiscal_period_not_found', severity: 'warning' },
  OPENING_ALREADY_EXISTS:     { key: 'errors.business.opening_already_exists',  severity: 'warning' },
  // Deshacer un import de diario desde el Centro de Datos.
  IMPORT_BATCH_ALREADY_UNDONE:   { key: 'errors.business.import_batch_already_undone',   severity: 'warning' },
  IMPORT_BATCH_HAS_DEPENDENCIES: { key: 'errors.business.import_batch_has_dependencies', severity: 'warning' },
  // Import de diario/apertura que no ha escrito nada: sin esto caían al 422 genérico
  // («Revisa los campos marcados»), y aquí no hay ningún campo marcado.
  ACCOUNTS_UNRESOLVED: { key: 'errors.business.accounts_unresolved', severity: 'warning' },
  IMPORT_CONFLICTS: { key: 'errors.business.import_conflicts', severity: 'warning' },
  // Asiento de saldo inicial de una cuenta bancaria (erp#2360). Los 409 no son
  // «duplicado» y los 422 no tienen campo que revisar: sin mapeo caerían a esos
  // genéricos y el modal no diría por qué no se puede contabilizar.
  // Dieta aprobada o pagada: ya cuenta en nómina y en el modelo 190 (api#3187).
  EXPENSE_NOT_DELETABLE:          { key: 'errors.business.expense_not_deletable',          severity: 'warning' },
  BANK_OPENING_POSTED:            { key: 'errors.business.bank_opening_posted',            severity: 'warning' },
  BANK_OPENING_IMPORTED:          { key: 'errors.business.bank_opening_imported',          severity: 'warning' },
  BANK_OPENING_LEDGER_NOT_EMPTY:  { key: 'errors.business.bank_opening_ledger_not_empty',  severity: 'warning' },
  BANK_OPENING_DATE_INVALID:      { key: 'errors.business.bank_opening_date_invalid',      severity: 'warning' },
  BANK_OPENING_OPEN_BANKING:      { key: 'errors.business.bank_opening_open_banking',      severity: 'warning' },
  BANK_OPENING_ZERO_BALANCE:      { key: 'errors.business.bank_opening_zero_balance',      severity: 'warning' },
  BANK_OPENING_NEGATIVE_BALANCE:  { key: 'errors.business.bank_opening_negative_balance',  severity: 'warning' },
  BANK_MOVEMENT_RECONCILED:       { key: 'errors.business.bank_movement_reconciled',       severity: 'warning' },
  BANK_OPENING_CURRENCY_MISMATCH: { key: 'errors.business.bank_opening_currency_mismatch', severity: 'warning' },
  // Anulación del asiento de saldo inicial.
  BANK_OPENING_NOT_POSTED:        { key: 'errors.business.bank_opening_not_posted',        severity: 'warning' },
  BANK_OPENING_ALREADY_REVERSED:  { key: 'errors.business.bank_opening_already_reversed',  severity: 'warning' },
  BANK_ACCOUNT_NOT_FOUND:         { key: 'errors.resource.not_found',                      severity: 'warning' },
  // Mismo hueco que PERIOD_LOCKED arriba: `assertJournalEntryLinesPostable`
  // (journalEntryPostabilityGuard.ts) lanza 422 con este code al crear/editar
  // un asiento manual contra una cuenta de grupo, inexistente o con subcuentas.
  // Sin este mapeo caía al fallback de status 422 (errors.validation.generic
  // → "Revisa los campos marcados"), perdiendo el motivo real y sin resaltar
  // ningún campo.
  JOURNAL_ENTRY_ACCOUNT_NOT_POSTABLE: { key: 'errors.business.journal_entry_account_not_postable', severity: 'warning' },
  // verix-api#3993 — contabilizar un gasto (p. ej. corrigiendo su cuenta desde la cola
  // «Por hacer») que el gate de cumplimiento bloquea: la cuota de IVA no cuadra con
  // base × tipo, o falta o no es válido el NIF del proveedor. Antes la API lo devolvía
  // como 500; ahora es 422 con este code, y sin mapeo caería al «Revisa los campos
  // marcados» del 422 genérico, que no dice que el problema es la propia factura.
  RECEIVED_INVOICE_COMPLIANCE_BLOCKED: { key: 'errors.business.received_invoice_compliance_blocked', severity: 'warning' },
  // El mismo hueco, en "Crear gasto" desde un escaneo OCR. `expenseReceptorGate`
  // rechaza con este code cuando el documento no está verificado a nombre de la
  // empresa, y sin mapeo caía al genérico de 422 — «Revisa los campos marcados»,
  // que aquí es doblemente inútil: no hay ningún campo marcado (el rechazo no es
  // de un campo del formulario, es del documento entero) y el usuario no puede
  // saber que el problema es el receptor.
  //
  // Medido en dev: el backend responde «El receptor de la factura no coincide con
  // el NIF de tu empresa» y el usuario leía «Algunos datos necesitan tu atención».
  // Ese `message` NO se puede pintar —la cabecera de este fichero es tajante y el
  // motivo es bueno: es texto libre del backend—, así que el copy se traduce aquí
  // desde el `code`, como el resto.
  //
  // El gate tiene CUATRO razones (`invoice_issued`, `missing_receptor`,
  // `tenant_mismatch` y `ticket_sin_receptor`). Este copy es el de respaldo y vale
  // para las tres primeras; `ticket_sin_receptor` tiene el suyo en `CODE_REASON_TO_KEY`
  // porque un ticket NO es «una factura a nombre de otra empresa» y tiene salida
  // (registrarlo con el IVA no deducible). `reason` llega por `POST /:id/create-expense`
  // y por los items del lote; si no llega, se usa este.
  OCR_VALIDATION:             { key: 'errors.business.ocr_receptor_unverified',            severity: 'warning' },
  // `DELETE /api/expenses/:id` de un gasto contabilizado (verix-api#3207). Sale si la lista estaba
  // desactualizada o en el borrado en bloque; tiene salida: cancelar el gasto revierte su asiento.
  EXPENSE_POSTED:             { key: 'errors.business.expense_posted',                     severity: 'warning' },
  // `PUT /api/expenses/:id` que cambia importe/IVA/fecha/cuenta de un gasto contabilizado
  // (verix-api#3269). Código propio: el copy de EXPENSE_POSTED dice «no se puede borrar».
  EXPENSE_POSTED_IMMUTABLE:   { key: 'errors.business.expense_posted_immutable',           severity: 'warning' },
  // `POST /api/expenses/:id/correct` — corregir la contabilización de un gasto. Los que
  // comparten código con otros flujos (EXPENSE_CANCELLED, DUPLICATE_EXPENSE) van en la vía
  // `correccion`, más abajo: aquí su copy hablaría de pagar o de dar de alta.
  EXPENSE_NOT_POSTED:              { key: 'errors.business.expense_not_posted',              severity: 'info' },
  EXPENSE_REJECTED:                { key: 'errors.business.expense_rejected',                severity: 'warning' },
  EXPENSE_CONCURRENT_CHANGE:       { key: 'errors.business.expense_concurrent_change',       severity: 'warning' },
  EXPENSE_TAX_FILING_PRESENTED:    { key: 'errors.business.expense_tax_filing_presented',    severity: 'warning' },
  EXPENSE_SII_SUBMITTED:           { key: 'errors.business.expense_sii_submitted',           severity: 'warning' },
  EXPENSE_PAID_SETTLEMENT_CHANGED: { key: 'errors.business.expense_paid_settlement_changed', severity: 'warning' },
  // `POST /api/expenses/:id/pay` (agentic-erp#2755). El backend los manda en `error`, sin
  // `code`; `MarkExpensePaidDialog` los sube a `code` antes de llegar aquí. Sin mapeo, los
  // dos caían al genérico del 409 («ya existe / duplicado»).
  ALREADY_PAID:               { key: 'errors.business.expense_already_paid',               severity: 'info' },
  EXPENSE_CANCELLED:          { key: 'errors.business.expense_cancelled_not_payable',      severity: 'warning' },
  // Sin tipo de cambio no se contabiliza nada: nóminas (`/api/payslips/:id/pay`, `/payrolls/:id`, `regenerate-entry`) y, desde
  // api#3710, gasto, pago de gasto y cobro de factura (estos mandan `details.{base,quote,date}` → `description_detalle`). La salida
  // es pedir el tipo a soporte: un tenant ya no puede darlo de alta (api#3409). Con devengo contabilizado la divisa de una nómina ya
  // no se puede cambiar. (BANK_ACCOUNT_NOT_FOUND ya estaba mapeado.)
  EXCHANGE_RATE_NOT_FOUND:    { key: 'errors.business.exchange_rate_not_found',            severity: 'warning' },
  PAYROLL_CURRENCY_LOCKED:    { key: 'errors.business.payroll_currency_locked',            severity: 'warning' },
  PAYROLL_BANK_CURRENCY_MISMATCH: { key: 'errors.business.payroll_bank_currency_mismatch',  severity: 'warning' },
  SETTLEMENT_EXCEEDS_BALANCE: { key: 'errors.business.settlement_exceeds_balance',         severity: 'warning' },
  // #2439 — los tres rechazos de `POST /api/refunds`. Significan cosas DISTINTAS
  // para quien los lee, y sin mapeo los tres caian al generico de su status:
  // el 422 en «Revisa los campos marcados» (no hay ningun campo que revisar) y
  // los dos 409 en «ya existe / duplicado», que para REFUND_IN_PROGRESS es
  // sencillamente falso.
  //
  //   REFUND_EXCEEDS_REFUNDABLE → «no cabe»: interpola {{refundable}}, la cifra
  //     que el backend calcula en #2655 y que es justo lo accionable.
  //   REFUND_DUPLICATE          → «esto ya lo hiciste».
  //   REFUND_IN_PROGRESS        → «espera al que hay»: reintentar SI es correcto,
  //     asi que el copy invita a ello. Confundirlo con el anterior haria que el
  //     usuario abandonase una operacion legitima.
  REFUND_EXCEEDS_REFUNDABLE:  { key: 'errors.business.refund_exceeds_refundable', severity: 'warning' },
  REFUND_DUPLICATE:           { key: 'errors.business.refund_duplicate',          severity: 'warning' },
  REFUND_IN_PROGRESS:         { key: 'errors.business.refund_in_progress',        severity: 'info' },
  // El 400 de una empresa sin cuenta bancaria predeterminada. Caia en
  // `errors.validation.generic` — «Revisa los campos marcados» — cuando lo que
  // hay que hacer es ir a Ajustes y marcar una cuenta.
  NO_DEFAULT_BANK_ACCOUNT:    { key: 'errors.business.no_default_bank_account',    severity: 'warning' },
  // verix-api#3271 — 400 de negocio que salían SIN `code` y caían en «Revisa los
  // campos marcados», que no dice qué pasa ni hay campo marcado. El backend ya
  // redactaba el motivo en `error`, pero este resolver no lo lee (ver cabecera).
  SUPPLIER_NOT_DRAFT:         { key: 'errors.business.supplier_not_draft',         severity: 'warning' },
  SUPPLIER_NO_NIF:            { key: 'errors.business.supplier_no_nif',            severity: 'warning' },
  SUPPLIER_NIF_INVALID:       { key: 'errors.business.supplier_nif_invalid',       severity: 'warning' },
  BANK_STATEMENT_FORMAT_UNSUPPORTED: { key: 'errors.business.bank_statement_format_unsupported', severity: 'warning' },
  // Dos códigos y no uno: el remedio del general es partir el fichero, y la apertura
  // NO se puede partir — decirle «divídelo» sería mandarle a un callejón.
  IMPORT_TOO_MANY_ROWS:       { key: 'errors.business.import_too_many_rows',       severity: 'warning' },
  OPENING_TOO_MANY_ROWS:      { key: 'errors.business.opening_too_many_rows',      severity: 'warning' },
  IMPORT_EMPTY_MAPPING:       { key: 'errors.business.import_empty_mapping',       severity: 'warning' },
  VIES_EMISSION_COMPLIANCE_BLOCKED: { key: 'errors.business.vies_blocked',    severity: 'warning' },
  // BLOQUE 3A — validación al GUARDAR el contacto (distinta del gate de emisión de
  // arriba, que actúa al enviar la factura). `CONTACT_VIES_BLOCKED` solo se dispara
  // para operadores intracomunitarios (país UE ≠ ES o vat_number con prefijo UE):
  // una empresa española no se consulta, porque el alta en el ROI es voluntaria.
  CONTACT_VIES_BLOCKED:       { key: 'errors.business.contact_vies_blocked',       severity: 'warning' },
  CONTACT_TYPE_TAXID_MISMATCH: { key: 'errors.business.contact_type_taxid_mismatch', severity: 'warning' },
  CONTACT_VIES_OVERRIDE_FORBIDDEN: { key: 'errors.business.contact_vies_override_forbidden', severity: 'error' },
  // Baja de contactos: operar con una ficha dada de baja (crear factura/gasto/…) o
  // intentar ELIMINAR una que tiene documentos colgando. Las dos son situaciones
  // esperadas con salida clara (reactivar / dar de baja), no fallos → warning.
  CONTACT_INACTIVE:           { key: 'errors.business.contact_inactive',      severity: 'warning' },
  CONTACT_HAS_MOVEMENTS:      { key: 'errors.business.contact_has_movements', severity: 'warning' },
  // Mismo criterio para el resto de maestros: borrar algo que tiene documentos colgando
  // tiene salida (desactivar), así que es warning. Sin este mapeo caían al fallback de
  // STATUS_TO_KEY[409] = "ya existe / duplicado", que dice algo FALSO: un empleado con 24
  // nóminas no es un duplicado.
  ACCOUNT_HAS_ENTRIES:        { key: 'errors.business.account_has_entries',        severity: 'warning' },
  EMPLOYEE_HAS_RECORDS:       { key: 'errors.business.employee_has_records',       severity: 'warning' },
  PRODUCT_HAS_MOVEMENTS:      { key: 'errors.business.product_has_movements',      severity: 'warning' },
  BANK_ACCOUNT_HAS_MOVEMENTS: { key: 'errors.business.bank_account_has_movements', severity: 'warning' },
  // Subcuenta auxiliar de un contacto. Sin estas tres entradas caian a STATUS_TO_KEY:
  // el 400 decia "Revisa los campos marcados" (no hay ningun campo marcado: el
  // problema es el prefijo) y el 409 "Este registro ya existe - edita el existente"
  // (que manda a editar el CONTACTO cuando lo duplicado es la CUENTA).
  AUX_ACCOUNT_WRONG_GROUP:    { key: 'errors.business.aux_account_wrong_group',    severity: 'warning' },
  AUX_ACCOUNT_COLLISION:      { key: 'errors.business.aux_account_collision',      severity: 'warning' },
  AUX_ACCOUNT_ROLE_INACTIVE:  { key: 'errors.business.aux_account_role_inactive',  severity: 'warning' },
  // Desactivar una cuenta contable cableada como destino por defecto. Sin mapear, un 409
  // cae en `errors.resource.duplicate` ("ya existe"), que aquí sería sencillamente falso.
  ACCOUNT_IN_USE_AS_DEFAULT:  { key: 'errors.business.account_in_use_as_default',  severity: 'warning' },
  // #648 (c) — gate de compliance del gasto (NIF emisor / desglose IVA). El detalle
  // por-campo lo aplica `applyServerErrors` (issues→campo); este toast es el resumen.
  EXPENSE_COMPLIANCE_BLOCKED: { key: 'errors.business.expense_compliance_blocked', severity: 'warning' },
  // #1213 — serie de numeración inexistente para la factura (NO implica "crea la
  // serie F": el code puede ser huérfano aunque existan otras series).
  INVOICE_SERIES_NOT_FOUND:   { key: 'errors.business.invoice_series_missing',     severity: 'warning' },
  // Guard de next_number al editar una serie (PUT /invoice-series): el valor
  // pedido es ≤ al MAX ya emitido del año en curso. Interpola
  // details.max_assigned / details.min_allowed.
  INVOICE_SERIES_NEXT_NUMBER_TOO_LOW: { key: 'errors.business.invoice_series_next_number_too_low', severity: 'warning' },
  INVOICE_STOCK_NO_WAREHOUSE: { key: 'errors.business.invoice_stock_no_warehouse', severity: 'warning' },
  INVOICE_STOCK_INSUFFICIENT: { key: 'errors.business.invoice_stock_insufficient', severity: 'warning' },
  // Rate limit
  // ── Certificado digital de empresa ───────────────────────────────────────
  // Los 11 codigos que emite `fiscal-certificates` no estaban mapeados: TODOS
  // caian al generico de 400 —«Revisa los campos marcados»— que ademas habla de
  // campos marcados donde no hay ninguno, solo un fichero y una contraseña. El
  // backend ya redacta el motivo real; lo que faltaba era que llegase.
  //
  // Visto en dev el 18-08-2026 subiendo un certificado de otra entidad: el
  // usuario leia «Revisa los campos marcados · 400 · FISCAL_CERT_THIRD_PARTY»
  // mientras el backend decia exactamente cual era el problema.
  FISCAL_CERT_THIRD_PARTY:    { key: 'errors.fiscal_cert.third_party',           severity: 'warning' },
  FISCAL_CERT_UNRECOGNIZED_OWNERSHIP: { key: 'errors.fiscal_cert.unrecognized_ownership', severity: 'warning' },
  FISCAL_CERT_EXPIRED:        { key: 'errors.fiscal_cert.expired',               severity: 'warning' },
  FISCAL_CERT_BAD_PASSWORD:   { key: 'errors.fiscal_cert.bad_password',          severity: 'warning' },
  FISCAL_CERT_FILE_MISSING:   { key: 'errors.fiscal_cert.file_missing',          severity: 'warning' },
  // verix-api#3271 — el alta sin contraseña era el único 400 del alta sin `code`.
  FISCAL_CERT_PASSWORD_MISSING: { key: 'errors.fiscal_cert.password_missing',    severity: 'warning' },
  FISCAL_CERT_UNSUPPORTED_ENCRYPTION: { key: 'errors.fiscal_cert.unsupported_encryption', severity: 'warning' },
  FISCAL_CERT_INCOMPLETE:     { key: 'errors.fiscal_cert.incomplete',            severity: 'warning' },
  FISCAL_CERT_UNREADABLE:     { key: 'errors.fiscal_cert.unreadable',            severity: 'warning' },
  FISCAL_CERT_MANDATE_REQUIRED: { key: 'errors.fiscal_cert.mandate_required',    severity: 'warning' },
  // F1d1 · con No VERI*FACTU el certificado FIRMA: la subida exige que sea cualificado y de un
  // prestador de la TSL española. Sin entrada caían al genérico del 400, «Revisa los campos
  // marcados», sobre una pantalla donde no hay nada marcado.
  FISCAL_CERT_NOT_QUALIFIED:  { key: 'errors.fiscal_cert.not_qualified',         severity: 'warning' },
  FISCAL_CERT_ISSUER_NOT_TRUSTED: { key: 'errors.fiscal_cert.issuer_not_trusted', severity: 'warning' },
  // El gate de EMISIÓN (verix-api, 422 FISCAL_CERT_REQUIRED_TO_EMIT). Los diez de arriba
  // son de SUBIR el certificado; éste es de EMITIR con él, y faltaba: cuando el backend
  // cortaba la emisión, el usuario leía el genérico «Esta acción no se puede completar en
  // este momento» — que no dice qué falta ni dónde se arregla.
  FISCAL_CERT_REQUIRED_TO_EMIT: { key: 'errors.fiscal_cert.required_to_emit',   severity: 'warning' },
  FISCAL_CERT_MISSING:        { key: 'errors.fiscal_cert.missing',               severity: 'warning' },
  // F5 (No VERI*FACTU) · la precarga del certificado falla por KMS o S3 (503). Sin esta
  // entrada caía en `STATUS_TO_KEY[503]` = «Esta sección no está disponible», que es falso:
  // la sección está; lo que no se ha podido leer es el certificado.
  FISCAL_CERT_UNAVAILABLE:    { key: 'errors.fiscal_cert.unavailable',           severity: 'error' },
  // Los rechazos con registro de F1a. Aquí va el copy por defecto de cada uno; la variante
  // por vía y por `motivo` la eligen las tablas `CODE_VIA_*` de más abajo.
  FACTURA_NO_ANULADA_SIN_REGISTRO: { key: 'errors.registro.anulacion.firma_fallida', severity: 'error' },
  // Seguimiento de F1a (E4): «Registrar» y `/verifactu/submit` con la firma fallida. Va
  // también aquí porque `/verifactu/submit` se llama sin vía.
  REGISTRO_NO_GENERADO:       { key: 'errors.registro.registro.firma',           severity: 'error' },
  // 🔴 El gate de emision (403 del backend: no puedes emitir sin certificado). SIN esta
  // entrada caia al generico del status 403 —«No tienes permiso para esta accion · puedes
  // concederlo desde Roles y Permisos»— y mandaba al usuario a Roles y Permisos a buscar
  // un permiso que no existe, cuando lo que le falta es subir su certificado. El backend
  // manda el texto correcto, pero este resolver NUNCA lee `message` (ver cabecera), asi
  // que el copy tiene que salir de aqui. Mismo fallo que ya documentan NO_ACTIVE_COMPANY
  // y FISCAL_CERT_THIRD_PARTY mas arriba: un code sin mapear no es un copy que falta, es
  // un copy EQUIVOCADO. La variante 'expired' la elige CODE_REASON_TO_KEY; esta entrada
  // es el default y cubre que `reason` no llegue.
  EMISSION_REQUIRES_CERT:     { key: 'errors.fiscal_cert.missing',               severity: 'warning' },
  // ── 🔴 LA EMISIÓN QUE NO LLEGA A OCURRIR (verix-api 422 FACTURA_NO_EMITIDA_SIN_REGISTRO) ──
  //
  // En VERI*FACTU, sin registro de facturación no se expide: la reserva del eslabón corre
  // DENTRO de la transacción que asigna el número, así que si falla revierte la transacción
  // entera y la factura se queda en borrador SIN consumir número (verix-api#3229, art. 8.1
  // RRSIF). El backend lo devuelve con su código propio y su texto.
  //
  // 🔴 SIN ESTA ENTRADA el usuario leía el genérico del 422 — «Revisa los campos marcados ·
  // Algunos datos necesitan tu atención antes de continuar»— y eso es FALSO, no incompleto:
  // NO HAY NINGÚN CAMPO QUE REVISAR. La factura no se ha emitido por un fallo nuestro, y lo
  // que la persona necesita saber es justo lo contrario de lo que decía ese texto: que no se
  // ha perdido ni se ha consumido nada. Es el caso que la cabecera de este fichero ya
  // describe —un code sin mapear no es un copy que falta, es un copy EQUIVOCADO— en su forma
  // más cara, porque el genérico manda a buscar un problema que no existe.
  //
  // `error` y no `warning` a propósito: no ha hecho nada mal quien pulsó. Con `warning` caería
  // en el mismo cajón visual que «completa este dato», que es la lectura que hay que evitar.
  //
  // El literal es el VALIDADO POR LA ASESORÍA (vía Adrián, 16-10-2026) y está fijado letra a
  // letra en `literalFacturaNoEmitida.test.ts`. ⚠️ El backend tiene su propia redacción en
  // `registroObligatorio.ts` —mismos hechos, otras palabras— y el cliente NUNCA la lee (este
  // resolvedor no toca `err.message`): la unificación de las dos copias va en verix-api#3554
  // y agentic-erp#3003, no aquí.
  FACTURA_NO_EMITIDA_SIN_REGISTRO: { key: 'errors.business.factura_no_emitida_sin_registro', severity: 'error' },

  // ── Cambio de modalidad VERI*FACTU (verix-api#3099 y #3100) ────────────────
  // Los tres los rechaza `PUT /api/companies/me`, y hasta hoy NINGUNO estaba mapeado:
  // el usuario que pulsaba «Activar VeriFACTU» en el paso 4 veia el generico «Esta
  // accion no se puede completar en este momento» sobre una accion que el servidor
  // rechaza SIEMPRE. Es el caso que este fichero ya describe arriba — un code sin
  // mapear no es un copy que falta, es un copy EQUIVOCADO.
  //
  // `info` y no `warning` en el primero a proposito: no ha ido nada mal ni hay nada
  // que corregir. La activacion todavia no existe en esta version, y el regimen
  // transitorio es el que corresponde hoy.
  VERIFACTU_MODE_SWITCH_DISABLED:     { key: 'errors.verifactu_mode.switch_disabled',   severity: 'info' },
  VERIFACTU_EMERGENCY_STOP:           { key: 'errors.verifactu_mode.emergency_stop',   severity: 'warning' },
  VERIFACTU_STOP_CONFIG_UNAVAILABLE:  { key: 'errors.verifactu_mode.stop_unavailable', severity: 'error' },
  VERIFACTU_STOP_CONFIG_INVALID:      { key: 'errors.verifactu_mode.stop_unavailable', severity: 'error' },
  // Este SI tiene accion, y existe: el paso 3 de esta misma pantalla sube el cert.
  VERIFACTU_ACTIVATION_REQUIRES_CERT: { key: 'errors.verifactu_mode.requires_cert',     severity: 'warning' },
  // Vaciar el campo metia a la empresa en VERI*FACTU sin pedirlo (los dos «no consta»
  // del backend significan lo contrario). El servidor lo rechaza; aqui se explica.
  VERIFACTU_MODE_CANNOT_BE_CLEARED:   { key: 'errors.verifactu_mode.cannot_be_cleared', severity: 'warning' },

  // ── Pieza (k) de la 1.0.1: la pantalla de activacion y la de salida ─────────
  // Los nueve los produce una de las dos pantallas nuevas. Van mapeados EN LA MISMA
  // entrega que las pantallas, no despues: un code sin mapear no es un copy que falta
  // —es el copy EQUIVOCADO—, y el generico «Esta accion no se puede completar en este
  // momento» sobre un rechazo que SI tiene salida deja al usuario sin la salida.
  //
  // `info` donde no ha ido nada mal y `warning` donde hay algo que hacer. Solo uno es
  // `error`: el que es configuracion nuestra.
  VERIFACTU_ACTIVATION_INCOMPLETE_FISCAL_DATA: { key: 'errors.verifactu_mode.incomplete_fiscal_data', severity: 'warning' },
  // Nuestro, no del usuario. Por eso `error` y por eso el texto dice que avise.
  VERIFACTU_ACTIVATION_NO_INCIDENT_OWNER:      { key: 'errors.verifactu_mode.no_incident_owner',      severity: 'error' },
  // Ceuta y Melilla. `info`: no hay nada que corregir, la version no lo cubre.
  VERIFACTU_ACTIVATION_TERRITORY_UNSUPPORTED:  { key: 'errors.verifactu_mode.territory_unsupported',  severity: 'info' },
  // 🆕 14-10-2026 · lo encontró el test de identidad con el backend: este código NO estaba
  // mapeado, así que una empresa sin territorio fiscal leía el genérico —«Esta acción no se
  // puede completar en este momento»— en vez del literal de Lara, que dice qué completar.
  VERIFACTU_ACTIVATION_TERRITORY_UNDETERMINED: { key: 'errors.verifactu_mode.territory_undetermined', severity: 'warning' },
  VERIFACTU_EMISION_YA_CESADA:                 { key: 'errors.verifactu_mode.ya_cesada',              severity: 'info' },
  VERIFACTU_CESE_SIN_ACTIVACION:               { key: 'errors.verifactu_mode.cese_sin_activacion',    severity: 'info' },
  // 🔴 NO es un fallo: la salida SI ha quedado registrada y la revisa una persona. Si
  // esto se pintara como error, el usuario creeria que no se ha guardado y lo repetiria.
  VERIFACTU_CESE_PENDIENTE_REVISION:           { key: 'errors.verifactu_mode.cese_pendiente_revision', severity: 'info' },
  // Lo devuelve el `PUT /companies/me` cerrado: la activacion pasa por su pantalla.
  VERIFACTU_ACTIVACION_REQUIERE_PANTALLA:      { key: 'errors.verifactu_mode.requiere_pantalla',      severity: 'info' },
  // El literal cambio mientras la pantalla estaba abierta. Su salida es recargar, y el
  // texto la dice.
  TEXTO_VERSION_CADUCADA:                      { key: 'errors.verifactu_mode.texto_caducado',         severity: 'warning' },
  TEXTO_PRODUCTO_PENDIENTE:                    { key: 'errors.verifactu_mode.texto_pendiente',        severity: 'info' },
  // F5 · los motivos de bloqueo de la activación que trajo F1a (No VERI*FACTU), y el resto
  // de codes de la activación que no tenían copy.
  VERIFACTU_NO_VERIFICABLE_DISABLED:            { key: 'errors.verifactu_mode.no_verificable_disabled',             severity: 'info' },
  VERIFACTU_NO_VERIFICABLE_REQUIERE_TRANSITORIO: { key: 'errors.verifactu_mode.no_verificable_requiere_transitorio', severity: 'info' },
  VERIFACTU_LOCAL_A_VERIFACTU_NO_DISPONIBLE:    { key: 'errors.verifactu_mode.local_a_verifactu_no_disponible',     severity: 'info' },
  VERIFACTU_ACTIVACION_SIN_FECHA_OBLIGACION:    { key: 'errors.verifactu_mode.sin_fecha_obligacion',                severity: 'warning' },
  VERIFACTU_CESE_DESDE_LOCAL_NO_DISPONIBLE:     { key: 'errors.verifactu_mode.cese_desde_local_no_disponible',      severity: 'info' },
  // F1b · las transiciones entre modalidades (`POST /activacion` con la modalidad destino) y la
  // renuncia (`POST /renuncia`). Los seis primeros son también `motivo_bloqueo` de la lectura, y
  // la pantalla los pinta con este mismo copy: el motivo y el 409 dicen lo mismo con las mismas
  // palabras. `info` donde no hay nada que corregir; `error` donde ha fallado algo nuestro.
  VERIFACTU_NO_VERIFICABLE_YA_ACTIVA:           { key: 'errors.verifactu_mode.no_verificable_ya_activa',            severity: 'info' },
  VERIFACTU_RENUNCIA_REQUERIDA:                 { key: 'errors.verifactu_mode.renuncia_requerida',                  severity: 'info' },
  VERIFACTU_RENUNCIA_EN_CURSO:                  { key: 'errors.verifactu_mode.renuncia_en_curso',                   severity: 'info' },
  VERIFACTU_OBLIGACION_SIN_DETERMINAR:          { key: 'errors.verifactu_mode.obligacion_sin_determinar',           severity: 'warning' },
  VERIFACTU_REGISTROS_PENDIENTES:               { key: 'errors.verifactu_mode.registros_pendientes',                severity: 'warning' },
  VERIFACTU_MODO_CAMBIADO:                      { key: 'errors.verifactu_mode.modo_cambiado',                       severity: 'warning' },
  // El 01 (inicio) o el 02 (fin) del libro de eventos no se ha podido registrar y el cambio se
  // ha deshecho. 503 reintentable; la variante de dato fuera de formato (422) va por `motivo`.
  VERIFACTU_INICIO_NO_REGISTRADO:               { key: 'errors.verifactu_mode.inicio_no_registrado',                severity: 'error' },
  VERIFACTU_FIN_NO_REGISTRADO:                  { key: 'errors.verifactu_mode.fin_no_registrado',                   severity: 'error' },
  VERIFACTU_FIN_REGISTRO_SIN_IMPORTES:          { key: 'errors.verifactu_mode.fin_registro_sin_importes',           severity: 'error' },
  VERIFACTU_RENUNCIA_SIN_VERIFACTU:             { key: 'errors.verifactu_mode.renuncia_sin_verifactu',              severity: 'info' },
  VERIFACTU_RENUNCIA_NO_NECESARIA:              { key: 'errors.verifactu_mode.renuncia_no_necesaria',               severity: 'info' },
  VERIFACTU_RENUNCIA_FUERA_DE_PLAZO:            { key: 'errors.verifactu_mode.renuncia_fuera_de_plazo',             severity: 'info' },
  VERIFACTU_RENUNCIA_YA_SOLICITADA:             { key: 'errors.verifactu_mode.renuncia_ya_solicitada',              severity: 'info' },
  // F5b · lanzar la comprobación de una cadena (F3, `POST /{registros|eventos}/comprobaciones`).
  // El 503 es nuestro (no se pudo encolar); los demás son estados, no fallos.
  COMPROBACION_NO_APLICA:                       { key: 'errors.verifactu_custodia.comprobacion_no_aplica',          severity: 'info' },
  COMPROBACION_TRAMO_INVALIDO:                  { key: 'errors.verifactu_custodia.comprobacion_tramo_invalido',     severity: 'warning' },
  COMPROBACION_EN_CURSO:                        { key: 'errors.verifactu_custodia.comprobacion_en_curso',           severity: 'info' },
  COMPROBACION_NO_ENCOLADA:                     { key: 'errors.verifactu_custodia.comprobacion_no_encolada',        severity: 'error' },
  // F5c · exportación de un periodo (F4b), requerimiento (F4c) y el XML de un registro VERI*FACTU
  // (F4a: no tiene firma local, lo conserva la AEAT; no es un fallo).
  EXPORTACION_NO_DISPONIBLE:                    { key: 'errors.verifactu_custodia.exportacion_no_disponible',       severity: 'error' },
  EXPORTACION_NO_APLICA:                        { key: 'errors.verifactu_custodia.exportacion_no_aplica',           severity: 'info' },
  EXPORTACION_PERIODO_VACIO:                    { key: 'errors.verifactu_custodia.exportacion_periodo_vacio',       severity: 'info' },
  EXPORTACION_DEMASIADO_GRANDE:                 { key: 'errors.verifactu_custodia.exportacion_demasiado_grande',    severity: 'warning' },
  EXPORTACION_EN_CURSO:                         { key: 'errors.verifactu_custodia.exportacion_en_curso',            severity: 'info' },
  EXPORTACION_NO_ENCOLADA:                      { key: 'errors.verifactu_custodia.exportacion_no_encolada',         severity: 'error' },
  REQUERIMIENTO_PERIODO_VACIO:                  { key: 'errors.verifactu_custodia.requerimiento_periodo_vacio',     severity: 'warning' },
  REQUERIMIENTO_EN_CURSO:                       { key: 'errors.verifactu_custodia.requerimiento_en_curso',          severity: 'info' },
  REQUERIMIENTO_NO_ENCOLADO:                    { key: 'errors.verifactu_custodia.requerimiento_no_encolado',       severity: 'error' },
  REQUERIMIENTO_NO_REANUDABLE:                  { key: 'errors.verifactu_custodia.requerimiento_no_reanudable',     severity: 'info' },
  REGISTRO_SIN_FIRMA_LOCAL:                     { key: 'errors.verifactu_custodia.registro_sin_firma_local',        severity: 'info' },
  COMPANY_NOT_FOUND:                           { key: 'errors.resource.not_found',                                 severity: 'warning' },

  // ── Bloqueos de VERI*FACTU con literal validado (erp#3003, erp#3019) ───────
  // Los cinco 422 del gate de emisión (`PATCH /api/invoices/:id/status`) y el 409 del
  // territorio bloqueado (`PUT /api/companies/me`). Sin mapear caían a los genéricos de
  // estado: «Algunos datos necesitan tu atención…» y «Este registro ya existe».
  //
  // El texto es el literal ENTERO de `verix-api` docs/legal/declaracion-responsable/
  // textos-producto-1.0.1.md (§ emisión) y de la guía de pruebas 1.0.1 (A1, A3–A6, B2),
  // partido en título y descripción SIN añadir ni quitar nada: título + « » + descripción
  // es el literal. Un cambio aquí que no venga de ese documento es FALLA de la DR.
  //
  // `literalEntero`: quien lo pinte lo pinta SOLO, sin anteponerle su propio título
  // (ver el medio-emitido de `FacturaFormV2`). Si no, el aviso deja de ser el literal.
  VERIFACTU_EMISION_EXIGE_ACTIVACION:          { key: 'errors.verifactu_bloqueo.exige_activacion',        severity: 'warning', literalEntero: true },
  VERIFACTU_EMISION_FORMA_JURIDICA_PENDIENTE:  { key: 'errors.verifactu_bloqueo.forma_juridica_pendiente', severity: 'warning', literalEntero: true },
  VERIFACTU_EMISION_TERRITORIO_PENDIENTE:      { key: 'errors.verifactu_bloqueo.territorio_pendiente',    severity: 'warning', literalEntero: true },
  // A soporte, no a cambiar el código postal: el literal lo dice así a propósito.
  VERIFACTU_EMISION_POSIBLE_DOMICILIO_FORAL:   { key: 'errors.verifactu_bloqueo.posible_domicilio_foral', severity: 'warning', literalEntero: true },
  // Ceuta y Melilla: no hay nada que corregir, la versión no lo cubre.
  VERIFACTU_EMISION_TERRITORIO_IPSI:           { key: 'errors.verifactu_bloqueo.territorio_ipsi',         severity: 'info',    literalEntero: true },
  VERIFACTU_FISCAL_TERRITORY_LOCKED:           { key: 'errors.verifactu_bloqueo.territorio_bloqueado',    severity: 'warning', literalEntero: true },

  // Este NO es culpa del usuario: es configuracion nuestra. `error`, no `warning`.
  FISCAL_CERT_KMS_MISCONFIGURED: { key: 'errors.fiscal_cert.kms_misconfigured',  severity: 'error' },

  // ── Anulacion de factura (pieza (j) de la 1.0.1) ───────────────────────────
  // La pantalla de VERI*FACTU pasa a anular por `POST /api/invoices/:id/cancel`, que es
  // la ruta que cierra la contabilidad. Empieza a devolver DOS codes que este fichero no
  // tenia, y van mapeados en la MISMA entrega: un code sin mapear no es un copy que
  // falta, es el copy EQUIVOCADO.
  //
  // El primero es el que mas importa y por eso es `info` y no `error`: no ha ido nada
  // mal. Es el criterio del asesor —una factura entregada se corrige con rectificativa,
  // no se anula— y su salida EXISTE y esta en esa misma pantalla.
  INVOICE_DELIVERED_USE_RECTIFICATION: { key: 'errors.invoice_cancel.delivered_use_rectification', severity: 'info' },
  //
  // ⚠️ `INVOICE_STATE_INVALID` NO se mapea aqui: YA lo esta arriba
  // (`errors.business.invoice_state_invalid`). Estuve a punto de duplicarlo porque mi
  // sonda pregunto por `INVALID_TRANSITION`, un code que no existe — el real lo dice el
  // backend. Lo cazo `tsc` con TS1117, no los 1756 tests, que pasaron enteros.
  //
  // Su literal es generico a proposito: el code lo comparte toda la app y este fichero
  // mapea POR CODE, no por pantalla. Pisarlo para la anulacion cambiaria el texto en
  // todos los demas sitios que lo devuelven.

  RATE_LIMIT_EXCEEDED:        { key: 'errors.rate_limit.generic',  severity: 'warning' },
  AI_QUOTA_EXCEEDED:          { key: 'errors.rate_limit.ai_quota', severity: 'info' },
  // Fase 2 planes — límite de cuota del plan superado (facturas/OCR/bancos/asientos).
  // Fallback por si algún call site no abre el PlanLimitModal (que lo dispara el
  // MutationCache global). Placeholders {{used}}/{{limit}}/{{resource}} desde details.
  PLAN_QUOTA_EXCEEDED:        { key: 'errors.plan.quota_reached',  severity: 'warning' },
  // Feature availability
  FEATURE_COMING_SOON:        { key: 'errors.feature.coming_soon',      severity: 'info' },
  FEATURE_MAINTENANCE:        { key: 'errors.feature.maintenance',      severity: 'warning' },
  FEATURE_NOT_CONFIGURED:     { key: 'errors.feature.not_configured',   severity: 'warning' },
  FEATURE_BETA_LOCKED:        { key: 'errors.feature.beta_unavailable', severity: 'info' },
  // Server / infra
  INTERNAL_ERROR:             { key: 'errors.server.generic',         severity: 'error' },
  DB_UNAVAILABLE:             { key: 'errors.server.maintenance',     severity: 'warning' },
  DEPENDENCY_DOWN:            { key: 'errors.server.dependency_down', severity: 'warning' },
  // G601 upload-response (Fase E.bis) — backend codes lanzados por
  // `G601UploadValidationError` (422), 409 conflict-recovery, walker
  // errors (ZIP) y storage. Todos warning excepto edge-cases internos +
  // storage que pintan destructive Alert. Reuso del KeyMap pattern
  // existente — UploadResponseForm consume via resolveErrorMessage.
  INVALID_METADATA_JSON:      { key: 'errors.g601.metadata_invalid_json',    severity: 'warning' },
  DUPLICATE_PDFS_IN_ZIP:      { key: 'errors.g601.duplicate_pdfs',           severity: 'warning' },
  DUPLICATE_NIFS_IN_METADATA: { key: 'errors.g601.duplicate_nifs_metadata',  severity: 'warning' },
  UNMATCHED_PDFS:             { key: 'errors.g601.unmatched_pdfs',           severity: 'warning' },
  METADATA_WITHOUT_PDF:       { key: 'errors.g601.metadata_without_pdf',     severity: 'warning' },
  PDF_WITHOUT_METADATA:       { key: 'errors.g601.pdf_without_metadata',     severity: 'warning' },
  RECEIPT_CSV_CONFLICT:       { key: 'errors.g601.receipt_csv_conflict',     severity: 'warning' },
  RECEIPT_VERDICT_CONFLICT:   { key: 'errors.g601.receipt_verdict_conflict', severity: 'warning' },
  ALREADY_REJECTED:           { key: 'errors.g601.already_rejected',         severity: 'warning' },
  ROW_FAILED:                 { key: 'errors.g601.row_failed',               severity: 'warning' },
  ROW_NOT_SUBMITTED:          { key: 'errors.g601.row_not_submitted',        severity: 'warning' },
  // 2 edge-cases defensivos sharing key — distintos códigos BE, mismo
  // significado user-facing ("contacta soporte"). Badge <code> mantiene
  // granularidad para triage soporte. severity=error → destructive Alert.
  INTERNAL_ROW_MISSING:       { key: 'errors.g601.internal_inconsistency',   severity: 'error' },
  INTERNAL_METADATA_MISSING:  { key: 'errors.g601.internal_inconsistency',   severity: 'error' },
  ZIP_MALFORMED:              { key: 'errors.g601.zip_malformed',            severity: 'warning' },
  NO_PDFS_IN_ZIP:             { key: 'errors.g601.zip_no_pdfs',              severity: 'warning' },
  // BE serializa lowercase (`{ error: 'storage_failed' }`) — verified
  // contra backend src/routes/asesor/index.ts:1060 (origin/dev).
  storage_failed:             { key: 'errors.g601.storage_failed',           severity: 'error' },
};

/**
 * Variantes de copy DENTRO de un mismo `code`, elegidas por `reason`.
 *
 * La regla de la casa es ramificar por `code` y nunca por «un campo al lado»
 * (verix-api#2047), porque esos campos se caen en el transporte sin avisar. Aqui no
 * aplica por dos motivos: `reason` viaja explicitamente en la lista blanca de
 * `buildApiError`, y el `code` sigue decidiendo QUE error es — `reason` solo elige entre
 * dos copys del mismo error. Si falta, se usa la entrada de CODE_TO_KEY y el mensaje
 * sigue siendo accionable.
 */
// 🔴 EXPORTADO el 15-10-2026, por el mismo motivo que CODE_TO_KEY: el test que compara los
// literales con el backend necesita el mapa REAL de variantes. Con una copia a mano, una causa
// nueva no entraría en ella y el test seguiría verde sobre un texto que nadie compara.
export const CODE_REASON_TO_KEY: Record<string, Record<string, KeyMap>> = {
  // Subir vs renovar: dos acciones distintas, dos pantallas distintas.
  EMISSION_REQUIRES_CERT: {
    no_cert: { key: 'errors.fiscal_cert.missing', severity: 'warning' },
    expired: { key: 'errors.fiscal_cert.expired', severity: 'warning' },
  },
  // 🔴 El cese pendiente de revisión: UN código, DOS motivos, y Lara entregó un texto para cada
  // uno (15-10-2026). Sin esta entrada el ERP tendría UNA copia para los dos casos y acertaría la
  // mitad de las veces —sin que nada se pusiera rojo, porque el texto saldría bien formado—.
  //
  // El backend manda `reason` con el valor de `MOTIVOS_REVISION_SALIDA`, y lo manda desde la MISMA
  // condición que elige el texto. Si faltara, se cae en la entrada de `CODE_TO_KEY`, que sigue
  // siendo accionable: dice que la salida queda en revisión, sin la causa.
  VERIFACTU_CESE_PENDIENTE_REVISION: {
    permanencia_provisional: { key: 'errors.verifactu_mode.cese_pendiente_revision_permanencia_provisional', severity: 'info' },
    sin_remision_aceptada: { key: 'errors.verifactu_mode.cese_pendiente_revision_sin_remision_aceptada', severity: 'info' },
  },
  // Un ticket sin los datos de la empresa no es «una factura a nombre de otra empresa»: se puede
  // registrar con el IVA no deducible si el usuario lo confirma. Ver `TicketSinReceptorAviso`.
  OCR_VALIDATION: {
    ticket_sin_receptor: { key: 'errors.business.ocr_ticket_sin_receptor', severity: 'warning' },
  },
};

// ── F5 · copy por VÍA (No VERI*FACTU) ────────────────────────────────────────
//
// El mismo `code` dice cosas distintas según la operación que lo produjo: un
// `FISCAL_CERT_REQUIRED_TO_EMIT` al emitir deja la factura en borrador sin número; al
// anular, la deja emitida con su asiento; en «Registrar», emitida sin registro. Un copy
// único por `code` mentiría en dos de las tres. La vía la pasa el llamador
// (`resolveErrorMessage(err, t, { via })` o `toastApiError(err, { via })`).
//
// 🔑 UNA regla para la ausencia de vía: **sin `via`, vía `emision`**. Es la vía de los
// llamadores que hoy no la pasan (formulario, ficha, drawer, proyectos) y no tiene
// entradas en `VIA_STATUS_TO_KEY`, así que un 500 sin vía sigue en el genérico. Para un
// `code` que no está en las tablas de la vía, todo sigue como antes. Lo fija
// `resolveErrorMessage.test.ts`.
export type ErrorVia = 'emision' | 'anulacion' | 'reembolso' | 'rectificativa' | 'registro' | 'correccion';
export interface ResolveErrorOptions { via?: ErrorVia }

const FIRMA_EMISION: KeyMap = { key: 'errors.registro.emision.firma_fallida', severity: 'error' };
const CODE_VIA_MOTIVO_TO_KEY: Record<ErrorVia, Record<string, Record<string, KeyMap>>> = {
  emision: {
    FACTURA_NO_EMITIDA_SIN_REGISTRO: {
      CERT_NO_VIGENTE_EN_FECHA_REGISTRO: { key: 'errors.registro.emision.cert_fecha', severity: 'warning' },
      FIRMA_FALLIDA: FIRMA_EMISION,
      FIRMA_NO_VERIFICA: FIRMA_EMISION,
      REGISTRO_NO_EXTRAIBLE: FIRMA_EMISION,
      IDENTIDAD_NO_PRECARGADA: { key: 'errors.registro.emision.cambio_concurrente', severity: 'warning' },
    },
    // F1b · la activación llama sin vía (= `emision`). Un dato que no cabe en el RegistroEvento
    // (422) no se arregla reintentando, que es lo que dice el copy por defecto (503).
    VERIFACTU_INICIO_NO_REGISTRADO: {
      EVENT_DATOS_FUERA_DE_FORMATO: { key: 'errors.verifactu_mode.inicio_fuera_de_formato', severity: 'warning' },
    },
    VERIFACTU_FIN_NO_REGISTRADO: {
      EVENT_DATOS_FUERA_DE_FORMATO: { key: 'errors.verifactu_mode.fin_fuera_de_formato', severity: 'warning' },
    },
  },
  anulacion: {
    FACTURA_NO_ANULADA_SIN_REGISTRO: {
      CERT_NO_VIGENTE_EN_FECHA_REGISTRO: { key: 'errors.registro.anulacion.cert', severity: 'warning' },
      EMISOR_SIN_NIF: { key: 'errors.registro.anulacion.sin_nif', severity: 'warning' },
      PRE_OBLIGATION: { key: 'errors.registro.anulacion.cambio_concurrente', severity: 'warning' },
      IDENTIDAD_NO_PRECARGADA: { key: 'errors.registro.anulacion.cambio_concurrente', severity: 'warning' },
    },
  },
  reembolso: {
    FACTURA_NO_EMITIDA_SIN_REGISTRO: {
      CERT_NO_VIGENTE_EN_FECHA_REGISTRO: { key: 'errors.registro.reembolso.cert', severity: 'warning' },
    },
  },
  rectificativa: {
    FACTURA_NO_EMITIDA_SIN_REGISTRO: {
      CERT_NO_VIGENTE_EN_FECHA_REGISTRO: { key: 'errors.registro.rectificativa.cert', severity: 'warning' },
      IDENTIDAD_NO_PRECARGADA: { key: 'errors.registro.rectificativa.cambio_concurrente', severity: 'warning' },
    },
  },
  registro: {
    REGISTRO_NO_GENERADO: {
      CERT_NO_VIGENTE_EN_FECHA_REGISTRO: { key: 'errors.registro.registro.cert_fecha', severity: 'warning' },
    },
  },
  correccion: {},
};

const CODE_VIA_REASON_TO_KEY: Record<ErrorVia, Record<string, Record<string, KeyMap>>> = {
  emision: {
    FISCAL_CERT_REQUIRED_TO_EMIT: {
      no_cert: { key: 'errors.fiscal_cert.required_to_emit', severity: 'warning' },
      expired: { key: 'errors.fiscal_cert.emit_expired', severity: 'warning' },
      not_yet_valid: { key: 'errors.fiscal_cert.emit_not_yet_valid', severity: 'warning' },
      // F1d · con No VERI*FACTU el certificado firma: cualificado (d1) y no revocado (d2).
      no_cualificado: { key: 'errors.fiscal_cert.emit_no_cualificado', severity: 'warning' },
      emisor_fuera_de_tsl: { key: 'errors.fiscal_cert.emit_emisor_fuera_de_tsl', severity: 'warning' },
      emisor_no_cualificado_en_fecha: { key: 'errors.fiscal_cert.emit_emisor_no_cualificado_en_fecha', severity: 'warning' },
      revoked: { key: 'errors.fiscal_cert.emit_revoked', severity: 'warning' },
      revocation_unknown: { key: 'errors.fiscal_cert.emit_revocation_unknown', severity: 'warning' },
    },
  },
  anulacion: {},
  reembolso: {},
  rectificativa: {},
  registro: {
    VERIFACTU_SIN_ESLABON: {
      EMISOR_SIN_NIF: { key: 'errors.registro.registro.sin_nif', severity: 'warning' },
    },
  },
  correccion: {},
};

const PARADA = ['VERIFACTU_EMERGENCY_STOP', 'VERIFACTU_STOP_CONFIG_UNAVAILABLE', 'VERIFACTU_STOP_CONFIG_INVALID'] as const;
const todas = (codes: readonly string[], m: KeyMap): Record<string, KeyMap> =>
  Object.fromEntries(codes.map((c) => [c, m]));

const CODE_VIA_TO_KEY: Record<ErrorVia, Record<string, KeyMap>> = {
  emision: {},
  anulacion: {
    FISCAL_CERT_REQUIRED_TO_EMIT: { key: 'errors.registro.anulacion.cert', severity: 'warning' },
    FISCAL_CERT_UNAVAILABLE: { key: 'errors.registro.anulacion.cert_no_disponible', severity: 'error' },
    FACTURA_NO_ANULADA_SIN_REGISTRO: { key: 'errors.registro.anulacion.firma_fallida', severity: 'error' },
    VERIFACTU_EMERGENCY_STOP: { key: 'errors.registro.anulacion.parada', severity: 'warning' },
    VERIFACTU_STOP_CONFIG_UNAVAILABLE: { key: 'errors.registro.anulacion.parada_desconocida', severity: 'error' },
    VERIFACTU_STOP_CONFIG_INVALID: { key: 'errors.registro.anulacion.parada_desconocida', severity: 'error' },
  },
  reembolso: {
    FISCAL_CERT_REQUIRED_TO_EMIT: { key: 'errors.registro.reembolso.cert', severity: 'warning' },
    FISCAL_CERT_UNAVAILABLE: { key: 'errors.registro.reembolso.cert_no_disponible', severity: 'error' },
    FACTURA_NO_EMITIDA_SIN_REGISTRO: { key: 'errors.registro.reembolso.firma_fallida', severity: 'error' },
    ...todas(PARADA, { key: 'errors.registro.reembolso.parada', severity: 'warning' }),
  },
  rectificativa: {
    FISCAL_CERT_REQUIRED_TO_EMIT: { key: 'errors.registro.rectificativa.cert', severity: 'warning' },
    FISCAL_CERT_UNAVAILABLE: { key: 'errors.registro.rectificativa.cert_no_disponible', severity: 'error' },
    FACTURA_NO_EMITIDA_SIN_REGISTRO: { key: 'errors.registro.rectificativa.firma_fallida', severity: 'error' },
    ...todas(PARADA, { key: 'errors.registro.rectificativa.parada', severity: 'warning' }),
  },
  registro: {
    FISCAL_CERT_REQUIRED_TO_EMIT: { key: 'errors.registro.registro.cert', severity: 'warning' },
    FISCAL_CERT_UNAVAILABLE: { key: 'errors.registro.registro.cert_no_disponible', severity: 'error' },
    REGISTRO_NO_GENERADO: { key: 'errors.registro.registro.firma', severity: 'error' },
    VERIFACTU_SIN_ESLABON: { key: 'errors.registro.registro.sin_eslabon', severity: 'warning' },
    ...todas(PARADA, { key: 'errors.registro.registro.parada', severity: 'warning' }),
  },
  // Corregir la contabilización de un gasto (`POST /api/expenses/:id/correct`).
  correccion: {
    EXPENSE_CANCELLED: { key: 'errors.business.expense_cancelled_not_correctable', severity: 'warning' },
    DUPLICATE_EXPENSE: { key: 'errors.business.expense_correction_duplicate', severity: 'warning' },
  },
};

/**
 * Paso 5: solo cuando el error llega «sin código útil» (`!code` o `INTERNAL_ERROR`). Va
 * ANTES de `CODE_TO_KEY` para que `INTERNAL_ERROR` no gane con `errors.server.generic`:
 * en estas vías la API no sabe (o no dice) qué quedó hecho, y el copy no puede afirmarlo.
 */
const VIA_STATUS_TO_KEY: Partial<Record<ErrorVia, Record<number, KeyMap>>> = {
  reembolso: { 500: { key: 'errors.registro.reembolso.sin_confirmar', severity: 'error' } },
  rectificativa: { 500: { key: 'errors.registro.rectificativa.sin_confirmar', severity: 'error' } },
  registro: { 500: { key: 'errors.registro.registro.sin_confirmar', severity: 'error' } },
};

const STATUS_TO_KEY: Record<number, KeyMap> = {
  400: { key: 'errors.validation.generic',         severity: 'warning' },
  401: { key: 'errors.auth.session_expired',       severity: 'error' },
  402: { key: 'errors.feature.upgrade_required',   severity: 'info' },
  403: { key: 'errors.auth.permission_denied',     severity: 'error' },
  404: { key: 'errors.resource.not_found',         severity: 'warning' },
  409: { key: 'errors.resource.duplicate',         severity: 'warning' },
  422: { key: 'errors.validation.generic',         severity: 'warning' },
  429: { key: 'errors.rate_limit.generic',         severity: 'warning' },
  501: { key: 'errors.feature.coming_soon',        severity: 'info' },
  503: { key: 'errors.feature.maintenance',        severity: 'warning' },
};

const FALLBACK: KeyMap = { key: 'errors.server.generic', severity: 'error' };

function formatRelativeDate(iso: string, locale: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(locale || 'es-ES', { day: '2-digit', month: 'long', hour: '2-digit', minute: '2-digit' });
}

export function resolveErrorMessage(
  err: ApiErrorLike | null | undefined,
  // Default to the global i18n binding so non-React callers (apiFetch, mutation
  // cache) can use this without threading a TFunction through every layer.
  t: TFunction = i18n.t.bind(i18n) as unknown as TFunction,
  opts: ResolveErrorOptions = {},
): ResolvedError {
  const e = err ?? {};
  const via: ErrorVia = opts.via ?? 'emision';
  const det = (e.details ?? {}) as { reason?: unknown; motivo?: unknown };
  // `apiFetch` solo sube `reason` del primer nivel; la API de F1a lo manda en `details`.
  // Si vienen los dos, manda `details.reason`: en el ítem `error` de `emit_drafts` el `reason`
  // de primer nivel es texto libre (`sanitizeError`) y el motivo de máquina va en `details`.
  const reason = (typeof det.reason === 'string' ? det.reason : undefined) ?? e.reason;
  const motivo = typeof det.motivo === 'string' ? det.motivo : undefined;
  const sinCodigoUtil = !e.code || e.code === 'INTERNAL_ERROR';
  const mapped = (e.code && motivo && CODE_VIA_MOTIVO_TO_KEY[via][e.code]?.[motivo])
    || (e.code && reason && CODE_VIA_REASON_TO_KEY[via][e.code]?.[reason])
    || (e.code && CODE_VIA_TO_KEY[via][e.code])
    || (e.code && reason && CODE_REASON_TO_KEY[e.code]?.[reason])
    || (sinCodigoUtil && typeof e.status === 'number' && VIA_STATUS_TO_KEY[via]?.[e.status])
    || (e.code && CODE_TO_KEY[e.code])
    || (typeof e.status === 'number' && STATUS_TO_KEY[e.status])
    || FALLBACK;

  // NOTA — los gates de autorización devuelven `required` con el código del permiso que
  // falta (`inventory.edit`, `crm.delete`…). Es tentador enseñarlo, pero es un
  // identificador de máquina y la cabecera de este fichero es tajante: aquí no entra
  // ninguna cadena técnica. Traducirlo tampoco sale gratis — los nombres legibles de los
  // permisos los sirve `/api/permissions` por empresa, y un toast no va a ir a buscarlos;
  // duplicar el catálogo en el frontend solo garantiza que acabe divergiendo del backend.
  // El mensaje dice DÓNDE se concede, que es la parte accionable.

  // Pull a few well-known fields out of `details` so `{{eta}}`,
  // `{{retry_after_sec}}` placeholders in the catalog Just Work.
  const tOpts: Record<string, unknown> = {};
  const details = (e.details ?? {}) as { eta?: unknown; retry_after_sec?: unknown; series?: unknown; productName?: unknown; aeat_detail?: unknown; powens_detail?: unknown; max_assigned?: unknown; min_allowed?: unknown; usage?: unknown; limit?: unknown; resource?: unknown; year?: unknown };
  if (typeof details.eta === 'string') tOpts.eta = formatRelativeDate(details.eta, i18n.language);
  if (typeof details.retry_after_sec === 'number') tOpts.retry_after_sec = details.retry_after_sec;
  // #1213 — `{{series}}` / `{{productName}}` placeholders para mensajes de facturación.
  if (typeof details.series === 'string') tOpts.series = details.series;
  if (typeof details.productName === 'string') tOpts.productName = details.productName;
  // `{{detail}}` — motivo real devuelto por AEAT (errors.business.aeat_rejected).
  if (typeof details.aeat_detail === 'string') tOpts.detail = details.aeat_detail;
  // `{{detail}}` — motivo real devuelto por Powens (errors.business.bank_connection_failed).
  // Comparte placeholder con aeat_detail: nunca coinciden en el mismo error.
  // Solo un motivo ÚTIL: descarta el genérico "API error <n>" (código maquinal que
  // Powens no cura, no es guía para el usuario) y los vacíos → el copy cae a su
  // fallback sin detail. Incluye su propio espacio final cuando existe, para que el
  // copy no deje un espacio colgante cuando no hay detail.
  if (typeof details.powens_detail === 'string') {
    const d = details.powens_detail.trim();
    tOpts.detail = d && !/^API error \d+$/.test(d) ? `${d} ` : '';
  }
  // `{{year}}` — ejercicio al que se refiere la guarda del importador de apertura
  // ("Falta el ejercicio 2026"). Sin él el mensaje no diría CUÁL crear.
  if (typeof details.year === 'number') tOpts.year = details.year;
  // `details.caso` — por qué ESTA factura exige el NIF del cliente (isp,
  // operacion_en_tai, registro_verifactu). Va como `context` de i18next: busca
  // `description_<caso>` y, si no existe, cae a `description`.
  const caso = (details as { caso?: unknown }).caso;
  if (e.code === 'CUSTOMER_NO_NIF' && typeof caso === 'string') tOpts.context = caso;
  // `details.currency` — la divisa sin tipo (INVOICE_FX_RATE_UNAVAILABLE). Solo con un código
  // ISO válido se usa la variante `description_currency` con `{{currency}}`; sin él, i18next
  // cae a `description`, que no la nombra, y nunca se ve el marcador sin rellenar.
  // `details.{base,quote,date}` — el par y la fecha sin tipo (EXCHANGE_RATE_NOT_FOUND, api#3710: gasto, pago,
  // cobro). Solo con los tres válidos se usa `description_detalle`; sin ellos (p. ej. la nómina, que no los
  // manda) i18next cae a `description`, que no los nombra, y nunca se ve un marcador sin rellenar.
  const par = details as { base?: unknown; quote?: unknown; date?: unknown };
  if (e.code === 'EXCHANGE_RATE_NOT_FOUND'
    && typeof par.base === 'string' && /^[A-Z]{3}$/.test(par.base)
    && typeof par.quote === 'string' && /^[A-Z]{3}$/.test(par.quote)
    && typeof par.date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(par.date)) {
    tOpts.base = par.base;
    tOpts.quote = par.quote;
    tOpts.date = par.date.split('-').reverse().join('/');
    tOpts.context = 'detalle';
  }
  const divisa = (details as { currency?: unknown }).currency;
  if (e.code === 'INVOICE_FX_RATE_UNAVAILABLE' && typeof divisa === 'string' && /^[A-Z]{3}$/.test(divisa)) {
    tOpts.currency = divisa;
    tOpts.context = 'currency';
    // agentic-erp#3140 — `details.date` es la fecha de devengo con la que se buscó el tipo: el texto
    // manda a pedirlo «para esa fecha», y sin decir CUÁL la acción no se puede hacer. Solo con una
    // fecha válida se usa `description_currency_fecha`; si no, queda la variante de siempre.
    const fecha = (details as { date?: unknown }).date;
    if (typeof fecha === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(fecha)) {
      tOpts.date = fecha.split('-').reverse().join('/');
      tOpts.context = 'currency_fecha';
    }
  }
  // `{{entidad}}` — el CIF de la entidad a la que pertenece el certificado
  // subido. Sin el, el copy de `third_party` no puede decir DE QUIEN es, que es
  // justo el dato que resuelve la duda del usuario.
  // Con el default, un backend que no mande el dato no deja el literal
  // `{{entidad}}` colgando en pantalla.
  const organizationId = (e as { organization_id?: unknown }).organization_id;
  tOpts.entidad = typeof organizationId === 'string' && organizationId ? organizationId : 'otra entidad';

  // `{{max_assigned}}` / `{{min_allowed}}` — guard de next_number de series.
  // `{{refundable}}` / `{{already_refunded}}` — cifras del tope de reembolso (#2655).
  // Llegan como number desde el backend; se formatean con el locale para no
  // enseñar `48.4` donde el resto de la aplicación dice `48,40 €`.
  const dinero = (n: number) => n.toLocaleString(i18n.language || 'es-ES', {
    style: 'currency', currency: 'EUR', minimumFractionDigits: 2,
  });
  // Con DEFAULT, igual que `entidad` arriba: un backend que no mande la cifra no
  // puede dejar `{{refundable}}` colgando en pantalla. Lo cazó el test antes de
  // salir — el copy degrada a una frase que sigue siendo cierta.
  const refundable = (details as { refundable?: unknown }).refundable;
  tOpts.refundable = typeof refundable === 'number' ? dinero(refundable) : 'lo que quede pendiente';
  const yaDevuelto = (details as { already_refunded?: unknown }).already_refunded;
  if (typeof yaDevuelto === 'number') tOpts.already_refunded = dinero(yaDevuelto);
  if (typeof details.max_assigned === 'number') tOpts.max_assigned = details.max_assigned;
  if (typeof details.min_allowed === 'number') tOpts.min_allowed = details.min_allowed;
  // Colisión de subcuenta auxiliar: DE QUIÉN es la cuenta y QUÉ código está libre. Sin
  // estos dos datos el copy solo podía decir «elige otro código libre», que es pedirle al
  // usuario que resuelva por su cuenta qué es una subcuenta auxiliar.
  //
  // Los tres con default, como `entidad` y `refundable` de arriba: el dueño puede llegar sin
  // nombre, y la sugerencia NO llega por dos caminos legítimos —la colisión por carrera
  // (nace dentro de una transacción que revierte) y el importador (una sugerencia que
  // ignorase los códigos que reclaman las filas siguientes del CSV volvería a pisar uno)—.
  // Ahí el copy tiene que ofrecer la otra salida (dejar el campo en blanco), no un hueco.
  if (e.code === 'AUX_ACCOUNT_COLLISION' || e.code === 'AUX_ACCOUNT_WRONG_GROUP') {
    const auxCode = (details as { code?: unknown }).code;
    const ownerName = (details as { owner_name?: unknown }).owner_name;
    const suggested = (details as { suggested_code?: unknown }).suggested_code;
    tOpts.code = typeof auxCode === 'string' && auxCode ? auxCode : 'indicada';
    tOpts.owner_name = typeof ownerName === 'string' && ownerName
      ? ownerName
      // Localizado: un default en castellano dejaría «belongs to otro contacto» en inglés.
      : i18n.t('errors.business.aux_account_other_contact', { defaultValue: 'otro contacto' });
    // Variante de i18next (`description_con_libre`): SOLO cuando hay código que ofrecer. Con
    // el `context` puesto a ciegas, un `suggested_code` ausente se interpola como cadena
    // vacía y el usuario lee «Puedes usar la , que está libre».
    if (typeof suggested === 'string' && suggested) {
      tOpts.suggested_code = suggested;
      tOpts.context = 'con_libre';
    }
  }
  // `{{used}}` / `{{limit}}` / `{{resource}}` — cuota de plan superada (Fase 2).
  // `usage`→`used`; `resource` se traduce a su etiqueta amigable (plan.limits.<x>).
  if (typeof details.usage === 'number') tOpts.used = details.usage;
  if (typeof details.limit === 'number') tOpts.limit = details.limit;
  if (typeof details.resource === 'string') {
    tOpts.resource = i18n.t(`plan.limits.${details.resource}`, { defaultValue: details.resource });
  }

  return {
    title: t(`${mapped.key}.title`, tOpts) as unknown as string,
    description: t(`${mapped.key}.description`, tOpts) as unknown as string,
    severity: mapped.severity,
    isGeneric: mapped.key.endsWith('.generic'),
    literalEntero: mapped.literalEntero === true,
  };
}
